import React, {useMemo, useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {getTextOrDefault, getImageUrlOrDefault} from 'app/Components/Block/BusinessDashboardBlock/utils/functions';
import {setSelectedHetPay, skipSelectCompanyStep} from 'app/Components/Block/PortalsBlock/portalSlice';
import {clearBusinessIndividualDataOnSelecthetPay} from 'app/Components/Block/CellcomAbroadLobbyBlock/cellcomAbroadBlockSlice';
import {setCurrentStep, saveToHistory} from 'app/Components/Block/BusinessDashboardBlock/BusinessDashboardSlice';
import CustomDashboardDropDown from '../CustomDashboardDropDown/CustomDashboardDropDown';
import {SELECT_TYPES, STEPS} from 'app/Components/Block/BusinessDashboardBlock/utils/constants';
import {isEmptyString} from '../../../utils/functions';
import {reportDWH} from 'app/Components/Block/PortalsBlock/portalSlice';
import gaParams from 'app/utilities/analytics/constants';
import {gaEvent} from 'app/utilities/analytics/tracking';

const DashboardHeader = ({
  contentData,
  userRole,
  currentStep,
  firstName,
  lastName,
  hetPayList,
  selectedHetPay,
  setSelectedHetPay,
  skipSelectCompanyStep,
  clearBusinessIndividualDataOnSelecthetPay,
  setCurrentStep,
  reportDWH,
  saveToHistory,
}) => {
  const [isMobile, setIsMobile] = useState(() => (window?.innerWidth < 768 ? true : false));
  const {welcomeText,headerTextColor, backgroundImage, backgroundImageMobile} = contentData || {};
  const backgroundImageFullUrl = isMobile ? getImageUrlOrDefault(backgroundImageMobile) : getImageUrlOrDefault(backgroundImage);
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : 'לקוח יקר';

  const getSelectType = useCallback(() => {
    if (hetPayList?.length === 1) {
      return SELECT_TYPES.ORGANIZATION_DASHBOARD_SINGLE_ELEMENT;
    }
    if (hetPayList?.length > 1) {
      if (!isEmptyString(selectedHetPay)) {
        return SELECT_TYPES.ORGANIZATION_DASHBOARD_MULTI_ELEMENTS;
      }
      return SELECT_TYPES.SELECT_ORGANIZATION;
    }
    return SELECT_TYPES.NONE;
  }, [hetPayList]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    setIsMobile(mediaQuery.matches);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const type = getSelectType();
    if (type === SELECT_TYPES.ORGANIZATION_DASHBOARD_SINGLE_ELEMENT && currentStep === SELECT_TYPES.SELECT_ORGANIZATION) {
      setCurrentStep(STEPS.ORGANIZATION_DASHBOARD);
    }
  }, []);

  useEffect(() => {
    reportDWH({
      actionType: 'Business Account List',
      status: 'Completed',
      messageText: 'Contact Entry Personal Area',
      param1: selectedHetPay,
      subArea: 'BusinessSelfCareArea',
    });
    gaEvent(gaParams.BUSINESS_PORTAL_ENTER_PORTAL.category, gaParams.BUSINESS_PORTAL_ENTER_PORTAL.eventType, userRole);
  }, []);

  const gaEventReport = useCallback((optionId) => {
    hetPayList.forEach((hetPay) => {
      if (hetPay.idNumber === optionId) {
        gaEvent(
          gaParams.BUSINESS_PORTAL_RENDER_AFTER_CHOICE.category,
          gaParams.BUSINESS_PORTAL_RENDER_AFTER_CHOICE.eventType,
          gaParams.BUSINESS_PORTAL_RENDER_AFTER_CHOICE.eventParams,
        );
      }
    });
  }, []);

  const handleOptionChange = useCallback((option) => {
    const optionId = option?.id;
    if (optionId) {
      setSelectedHetPay(optionId);
      skipSelectCompanyStep(true);
      clearBusinessIndividualDataOnSelecthetPay();
      reportDWH({
        actionType: 'Business Contact Lobby',
        status: 'Ongoing',
        messageText: 'Business Account Clicked (ח.פ.)',
        param1: selectedHetPay,
        subArea: 'BusinessSelfCareArea',
      });
      gaEventReport(optionId);
      saveToHistory();
    }
  }, []);

  const handleMenuIsOpen = useCallback(() => {
    reportDWH({
      actionType: 'Business Account List',
      status: 'Ongoing',
      messageText: 'Business Account List Displayed',
      subArea: 'BusinessSelfCareArea',
    });
  }, []);

  const handleSelectOrganization = useCallback((option) => {
    if (currentStep !== STEPS.ORGANIZATION_DASHBOARD) {
      setCurrentStep(STEPS.ORGANIZATION_DASHBOARD);
    }
    handleOptionChange(option);
  }, []);

  const dropdownProps = useMemo(() => {
    const selectType = getSelectType();
    switch (selectType) {
      case SELECT_TYPES.SELECT_ORGANIZATION:
        return {
          controlText: 'על איזה ח.פ מדובר?',
          onOptionSelect: handleSelectOrganization,
          initiallyOpen: true,
          defaultOption: null,
        };

      case SELECT_TYPES.ORGANIZATION_DASHBOARD_SINGLE_ELEMENT:
        const singleElement = hetPayList?.[0];
        return {
          controlText: singleElement ? `${singleElement?.id} - ${singleElement?.name}` : '',
          onOptionSelect: handleOptionChange,
          initiallyOpen: false,
          defaultOption: singleElement || null,
        };

      case SELECT_TYPES.ORGANIZATION_DASHBOARD_MULTI_ELEMENTS:
        const selectedOption = hetPayList?.find((opt) => opt.id === selectedHetPay);
        return {
          controlText: selectedOption ? `${selectedOption?.id} - ${selectedOption?.name}` : '',
          onOptionSelect: handleOptionChange,
          initiallyOpen: false,
          defaultOption: selectedOption || null,
        };

      default:
        return {
          controlText: 'על איזה ח.פ מדובר?',
          onOptionSelect: handleSelectOrganization,
          initiallyOpen: true,
          defaultOption: null,
        };
    }
  }, [hetPayList]);

  const containerStyle = useMemo(
    () => ({
      backgroundImage: backgroundImageFullUrl ? `url(${backgroundImageFullUrl})` : 'none',
      backgroundColor: backgroundImageFullUrl ? 'transparent' : 'var(--theme-light,#e2cff7)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
          '--header-text-color': headerTextColor?.value || '#000000',
    }),
    [backgroundImageFullUrl, headerTextColor],
  );

  return (
    <div className='dashboard-header-container' style={containerStyle}>
      <div className='dashboard-header'>
        <div className='header-content'>
          <span className='welcome-text'>{getTextOrDefault(welcomeText, 'שלום,')}</span>
          <span className='welcome-full-name'>{fullName}</span>
        </div>
        <div className='dashboard-body'>
          <CustomDashboardDropDown {...dropdownProps} options={hetPayList} onMenuOpenCallback={handleMenuIsOpen} />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentStep: state.BusinessDashboardSlice.currentStep,
  userRole: state.authentication.loginData.userDetails.role,
  firstName: state.portalsBlock.firstName,
  lastName: state.portalsBlock.lastName,
  hetPayList: state.portalsBlock.hetPaysNumbers,
  selectedHetPay: state.portalsBlock.selectedHetPay,
});

const mapDispatchToProps = {
  setSelectedHetPay,
  skipSelectCompanyStep,
  clearBusinessIndividualDataOnSelecthetPay,
  setCurrentStep,
  reportDWH,
  saveToHistory,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardHeader);
